import { Fragment, type FC, useState, useCallback } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';

// components
import { Link } from 'dibs-elements/exports/Link.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { PdpSharedDealerDetailsBioModalLazy } from './PdpSharedDealerDetailsBioModalLazy.tsx';

// types
import { type PdpSharedDealerDetailsBio_seller$data as Seller } from './__generated__/PdpSharedDealerDetailsBio_seller.graphql.ts';

// styles
import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './PdpSharedDealerDetailsBio.scss';

type Props = {
    seller: Seller;
    afterDisplay: boolean;
};

const PdpSharedDealerDetailsBioComponent: FC<Props> = ({ seller, afterDisplay }) => {
    const { isRewardsTier2, liveStorefrontProfile } = seller || {};
    const [isBioModalOpen, setIsBioModalOpen] = useState<boolean>(false);

    const openBioModal = useCallback(() => setIsBioModalOpen(true), []);
    const closeBioModal = useCallback(() => setIsBioModalOpen(false), []);

    if (!liveStorefrontProfile || !isRewardsTier2) {
        return null;
    }

    const { aboutUsUnbranded: aboutUs } = liveStorefrontProfile;

    return (
        <Fragment>
            {aboutUs && (
                <div
                    className={classnames(
                        styles.container,
                        dibsCss.sassyFontBody,
                        dibsCss.flex,
                        dibsCss.flexRow,
                        dibsCss.itemsStart
                    )}
                >
                    <span className={classnames(dibsCss.sassyFontBody, dibsCss.mAuto)}>
                        <p className={styles.text} dangerouslySetInnerHTML={{ __html: aboutUs }} />
                        <Link className={dibsCss.mtXsmall} onClick={openBioModal}>
                            <FormattedMessage
                                id="PdpSharedDealerDetailsBio.readMore"
                                defaultMessage="Read More"
                            />
                        </Link>
                        {afterDisplay && (
                            <PdpSharedDealerDetailsBioModalLazy
                                seller={seller}
                                isOpen={isBioModalOpen}
                                onClose={closeBioModal}
                            />
                        )}
                    </span>
                </div>
            )}
        </Fragment>
    );
};

export const PdpSharedDealerDetailsBio = createFragmentContainer(
    PdpSharedDealerDetailsBioComponent,
    {
        seller: graphql`
            fragment PdpSharedDealerDetailsBio_seller on Seller {
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                liveStorefrontProfile {
                    aboutUsUnbranded
                }
                ...PdpSharedDealerDetailsBioModalLazy_seller
            }
        `,
    }
);
